import RefreshIcon from "@mui/icons-material/Refresh";
import {
  CircularProgress,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import alqemiDataService from "../../api/AlqemiDataService";
import MatchStatus from "../components/MatchStatus";

type StatsData = {
  all: { counts: Record<string, number>; total: number };
  since_feb: { counts: Record<string, number>; total: number };
};

const CACHE_KEY = "matchStatsCache";
const CACHE_TTL = 6 * 60 * 60 * 1000; // 6 hours

function getCachedStats(): StatsData | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp > CACHE_TTL) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return data;
  } catch {
    localStorage.removeItem(CACHE_KEY);
    return null;
  }
}

function setCachedStats(data: StatsData) {
  localStorage.setItem(
    CACHE_KEY,
    JSON.stringify({ data, timestamp: Date.now() }),
  );
}

const STATUS_ORDER: string[] = [
  "matched",
  "consultant_review",
  "supervisor_review",
  "approved",
  "sent",
  "rejected",
  "timed_out",
];

export default function MatchStatsTab() {
  const [statsData, setStatsData] = useState<StatsData | null>(getCachedStats);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsView, setStatsView] = useState<"since_feb" | "all">("since_feb");

  const fetchStats = useCallback(async (force = false) => {
    if (!force) {
      const cached = getCachedStats();
      if (cached) {
        setStatsData(cached);
        return;
      }
    }
    setStatsLoading(true);
    try {
      const data = await alqemiDataService.getMatchStats();
      setStatsData(data);
      setCachedStats(data);
    } catch (error) {
      console.error("Error fetching match stats:", error);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!statsData) {
      fetchStats();
    }
  }, [fetchStats, statsData]);

  const currentStats = statsData ? statsData[statsView] : null;

  if (statsLoading && !statsData) {
    return (
      <Stack sx={{ alignItems: "center", py: 3 }}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  if (!statsData) {
    return (
      <Typography variant="body2" color="text.secondary">
        Failed to load stats.
      </Typography>
    );
  }

  return (
    <Stack sx={{ maxWidth: 400 }}>
      <Stack
        sx={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <ToggleButtonGroup
          value={statsView}
          exclusive
          onChange={(_, value) => {
            if (value !== null) setStatsView(value);
          }}
          size="small"
        >
          <ToggleButton value="since_feb">Since February 2026</ToggleButton>
          <ToggleButton value="all">All time</ToggleButton>
        </ToggleButtonGroup>
        <Tooltip title="Refresh stats">
          <IconButton
            size="small"
            onClick={() => fetchStats(true)}
            disabled={statsLoading}
          >
            {statsLoading ? <CircularProgress size={18} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Stack>

      <Stack sx={{ gap: 1 }}>
        {STATUS_ORDER.map((status) => (
          <Stack
            key={status}
            sx={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <MatchStatus status={status} />
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {currentStats?.counts[status] ?? 0}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Stack
        sx={{
          mt: 2,
          pt: 2,
          borderTop: "1px solid #e0e0e0",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          Total matches
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {currentStats?.total ?? 0}
        </Typography>
      </Stack>

      {statsView === "since_feb" && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: "block" }}
        >
          &quot;Matched&quot; status was introduced on 02.02.2026
        </Typography>
      )}
    </Stack>
  );
}
